import { useEffect, useState } from 'react';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { formatBCH, formatFiat } from '@/lib/bch';

interface CustomerDisplayProps {
  bchUri: string;
  fiatTotal: number;
  bchAmount: number;
  currency: string;
  merchantName: string;
  merchantTagline?: string;
  logoDataUrl?: string;
  brandColor: string;
  /** When set (e.g. "5.00 PUSD"), shown instead of the BCH amount. */
  assetLabel?: string;
  /** Optional icon shown next to the asset label. */
  assetIconUrl?: string;
  /** Wallet-scan instruction override. */
  scanHint?: string;
  onExit: () => void;
}

/**
 * Full-screen customer-facing display.
 * Shows only the QR code, amount, and merchant branding.
 * The merchant exits by tapping the top-left corner 3 times quickly.
 */
export function CustomerDisplay({
  bchUri,
  fiatTotal,
  bchAmount,
  currency,
  merchantName,
  merchantTagline,
  logoDataUrl,
  brandColor,
  assetLabel,
  assetIconUrl,
  scanHint,
  onExit,
}: CustomerDisplayProps) {
  const [tapCount, setTapCount] = useState(0);
  const qrSize = Math.min(window.innerWidth - 64, 320);

  // Reset tap counter after 2 seconds of inactivity
  useEffect(() => {
    if (tapCount === 0) return;
    const timer = setTimeout(() => setTapCount(0), 2000);
    return () => clearTimeout(timer);
  }, [tapCount]);

  // 3 taps in the exit zone to go back
  const handleExitTap = () => {
    const next = tapCount + 1;
    if (next >= 3) {
      onExit();
    } else {
      setTapCount(next);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-zinc-950">
      {/* Hidden exit zone — top-left corner, merchant taps 3x to exit */}
      <button
        onClick={handleExitTap}
        className="absolute top-0 left-0 w-20 h-20 z-50"
        aria-label="Exit customer display (tap 3 times)"
      >
        {/* Subtle indicator after first tap */}
        {tapCount > 0 && (
          <span className="absolute top-3 left-3 flex gap-0.5">
            {Array.from({ length: tapCount }).map((_, i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
            ))}
          </span>
        )}
      </button>

      {/* Branded header */}
      <div className="text-center mb-6">
        {logoDataUrl ? (
          <div className="flex justify-center mb-3">
            <img
              src={logoDataUrl}
              alt={merchantName}
              className="h-14 max-w-[180px] object-contain"
            />
          </div>
        ) : (
          <h1 className="text-2xl font-black text-zinc-900 dark:text-zinc-50 mb-1">
            {merchantName || 'BCHPAY.Cash'}
          </h1>
        )}
        {merchantTagline && (
          <p className="text-sm text-zinc-400">{merchantTagline}</p>
        )}
      </div>

      {/* Amount */}
      <div className="text-center mb-6">
        <p className="text-4xl font-black text-zinc-900 dark:text-zinc-50">
          {formatFiat(fiatTotal, currency)}
        </p>
        <p className="flex items-center justify-center gap-2 text-lg font-mono font-medium mt-1" style={{ color: brandColor }}>
          {assetLabel ? (
            <>
              {assetIconUrl && <img src={assetIconUrl} alt="" className="h-5 w-5 rounded-full" />}
              {assetLabel}
            </>
          ) : (
            `${formatBCH(bchAmount)} BCH`
          )}
        </p>
      </div>

      {/* QR Code */}
      <div className="bg-white rounded-3xl p-5 shadow-xl border border-zinc-100">
        <QRCodeCanvas
          value={bchUri}
          size={qrSize}
          level="M"
          className="rounded-xl"
        />
      </div>

      {/* Scan instruction */}
      <p className="text-sm text-zinc-400 mt-6 font-medium">
        {scanHint ?? 'Scan with any Bitcoin Cash wallet'}
      </p>

      {/* Pulsing indicator */}
      <div className="flex items-center gap-2 mt-4">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: brandColor }} />
          <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: brandColor }} />
        </span>
        <span className="text-sm text-zinc-400">Waiting for payment...</span>
      </div>

      {/* Powered by */}
      <p className="absolute bottom-6 text-xs text-zinc-300 dark:text-zinc-700">
        Powered by BCHPAY.Cash
      </p>
    </div>
  );
}
